/**
 * Tipos para los nuevos endpoints de disponibilidad (Rev2)
 * Integración de rev_disponibilidad_ahora, rev_disponibilidad_rango,
 * rev_disponibilidad_espacio, rev_disponibilidad_checkout
 */

// ============================================================================
// DESGLOSE DÍA A DÍA - Base para todos los endpoints
// ============================================================================

export interface DiaDesglose {
  // Identificación del día
  fecha: string // ISO date (YYYY-MM-DD)
  dia_semana: string // Nombre del día en español (lunes, martes, etc)

  // Disponibilidad
  disponible_dia: boolean // True si está disponible en ese día específico
  motivo_indisponibilidad?: string | null
  // "Reserva confirmada" | "Bloqueo temporal" | "Período no disponible" | null (si disponible)

  // Tarifas (variable según endpoint)
  tarifa_simple?: number // Tarifa para 1 unidad
  tarifa_multiple?: number // Tarifa para 2+ unidades
  nombre_tarifa_simple?: string // "Tarifa base", "Semana", etc
  nombre_tarifa_multiple?: string // "Tarifa base", "Fin de semana", etc

  // Para checkout (campos específicos)
  tarifa?: number // Tarifa única según cantidad_unidades
  nombre_tarifa?: string // Nombre de la tarifa única aplicada
}

// ============================================================================
// ENDPOINT: rev_disponibilidad_ahora (HOY sin tarifas)
// ============================================================================

export interface EspacioDisponibleHoy {
  id: string
  codigo: string
  nombre: string
  descripcion?: string
  disponible: boolean
  motivo_indisponibilidad?: string | null // Si no disponible, explica porqué

  // Tipo de espacio
  tipo_nombre?: string // Nombre del tipo (Cabaña, Camping, etc.)
  tipo_espacio_id?: string
  tipo_privado?: boolean // Si el tipo es privado (solo admin)

  // Tarifas de HOY
  tarifa_hoy?: number // Tarifa simple de HOY
  tarifa_multiple_hoy?: number // Tarifa múltiple de HOY (si existe)
  nombre_tarifa_hoy?: string // Nombre de la tarifa simple (ej: "Tarifa base", "Fin de semana")
  nombre_tarifa_multiple_hoy?: string // Nombre de la tarifa múltiple

  // Grid/Plano
  plano_x?: number
  plano_y?: number
  plano_ancho?: number
  plano_alto?: number
  plano_radio?: number
  grid_zona?: string
  grid_fila?: number
  grid_columna?: number
  plano_vertices?: Array<{ x: number; y: number }> | null

  // Comodidades
  capacidad_maxima: number
  comodidades: any[]
  imagenes?: string[]
  unidad_reserva?: string // 'noche' o 'dia'
  reservable?: boolean
  tarifas?: any[] // Para compatibilidad si el endpoint lo devuelve
}

// ============================================================================
// ENDPOINT: rev_disponibilidad_rango (Rango con desglose completo)
// ============================================================================

export interface EspacioDisponibleRango {
  id: string
  codigo: string
  nombre: string
  descripcion?: string

  // Tipo de espacio
  tipo_nombre?: string // Nombre del tipo (Cabaña, Camping, etc.)
  tipo_espacio_id?: string
  tipo_privado?: boolean // Si el tipo es privado (solo admin)

  // Disponibilidad general
  disponible: boolean // True si hay al menos 1 día disponible
  dias_disponibles: string[] // Array de fechas ISO disponibles
  dias_ocupados: string[] // Array de fechas ISO ocupadas
  total_dias_disponibles: number
  total_dias_rango?: number // Total de días en el rango consultado

  // Motivo de no disponibilidad (si aplica)
  motivo_indisponibilidad?: string | null

  // Desglose día a día (crítico para esta respuesta)
  desglose: DiaDesglose[] // Todos los días del rango con disponible_dia + motivo

  // Información del espacio
  unidad_reserva: 'noche' | 'dia'
  capacidad_maxima: number
  comodidades: any[]
  imagenes?: string[]
  reservable?: boolean

  // Grid/Plano
  plano_x?: number
  plano_y?: number
  plano_ancho?: number
  plano_alto?: number
  plano_radio?: number
  grid_zona?: string
  grid_fila?: number
  grid_columna?: number
  plano_vertices?: Array<{ x: number; y: number }> | null

  // Metadata
  empresa_id?: number
  [key: string]: any // Permitir otros campos del backend
}

// ============================================================================
// ENDPOINT: rev_disponibilidad_espacio (Espacio específico con desglose)
// ============================================================================

export interface DetalleEspacioDisponible {
  id: string
  codigo: string
  nombre: string
  descripcion?: string
  tipo_privado?: boolean // Si el tipo es privado (solo admin)

  // Disponibilidad del rango consultado
  disponible: boolean
  dias_disponibles: string[]
  dias_ocupados: string[]

  // Desglose día a día
  desglose: DiaDesglose[] // Todos los días con tarifa_simple, tarifa_multiple

  // Información del espacio
  unidad_reserva: 'noche' | 'dia'
  capacidad_maxima: number
  comodidades: any[]
  imagenes?: string[]

  // Grid/Plano
  plano_x?: number
  plano_y?: number
  plano_ancho?: number
  plano_alto?: number
  plano_radio?: number
  grid_zona?: string
  grid_fila?: number
  grid_columna?: number
  plano_vertices?: Array<{ x: number; y: number }> | null

  // Metadata
  empresa_id?: number
  [key: string]: any
}

// ============================================================================
// ENDPOINT: rev_disponibilidad_checkout (Validación final + desglose)
// ============================================================================

export interface CheckoutDisponibilidad {
  espacio_id: string
  espacio_nombre: string

  // Disponibilidad final
  disponible: boolean // False = error 409, true = proceder a pagar

  // Tarifa total calculada por backend
  tarifa_total: number // Total para todo el rango

  // Desglose actualizado (puede diferir del desglose anterior)
  desglose: DiaDesglose[] // Solo contiene tarifa + nombre_tarifa (según cantidad_unidades)

  // Información base
  fecha_entrada: string
  fecha_salida: string
  cantidad_unidades: number
  unidad_reserva: 'noche' | 'dia'

  // Plano
  plano_vertices?: Array<{ x: number; y: number }> | null

  [key: string]: any
}

// ============================================================================
// TIPOS AUXILIARES
// ============================================================================

export interface FiltrosDisponibilidad {
  fecha_entrada: string
  fecha_salida: string
  tipo_espacio_id?: string // Opcional para rango
  cantidad_huespedes?: number
}

export interface RangoFechas {
  fecha_entrada: string
  fecha_salida: string
}

// ============================================================================
// COLORES PARA MOTIVOS (Referencia para UI)
// ============================================================================

export const MOTIVO_COLORES = {
  'Reserva confirmada': '#ef4444', // Rojo
  'Bloqueo temporal': '#f97316', // Naranja
  'Período no disponible': '#9ca3af', // Gris
  'disponible': '#22c55e', // Verde
} as const

export type MotivoIndisponibilidad =
  | 'Reserva confirmada'
  | 'Bloqueo temporal'
  | 'Período no disponible'
  | null

/**
 * Obtener color para un motivo
 */
export function getMotivColor(motivo: MotivoIndisponibilidad): string {
  if (!motivo) return MOTIVO_COLORES.disponible
  return MOTIVO_COLORES[motivo as keyof typeof MOTIVO_COLORES] || MOTIVO_COLORES.disponible
}
